import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface LoadErrorStateProps {
  /** Short description of what failed to load, e.g. "deliverables". */
  what?: string;
  /** Optional detail message (usually the caught error's message). */
  message?: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * Shared load-failure state. Visually distinct from a genuine empty
 * state (destructive icon + retry action) so a network/RLS/permission
 * failure is never mistaken for "no data yet".
 */
export function LoadErrorState({ what, message, onRetry, className }: LoadErrorStateProps) {
  return (
    <Card role="alert" className={className}>
      <CardContent className="py-12 flex flex-col items-center justify-center text-center gap-3">
        <AlertTriangle className="h-10 w-10 text-destructive-text" aria-hidden="true" />
        <div>
          <p className="font-medium text-foreground">
            Couldn&apos;t load{what ? ` ${what}` : ''}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {message || 'Something went wrong while fetching this data.'}
          </p>
        </div>
        {onRetry ? (
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Inline (non-card) variant for use inside existing cards/widgets. */
export function InlineLoadError({ what, message, onRetry }: LoadErrorStateProps) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
      <AlertTriangle className="h-4 w-4 text-destructive-text shrink-0" aria-hidden="true" />
      <span className="text-foreground">
        Couldn&apos;t load{what ? ` ${what}` : ''}.
        {message ? <span className="text-muted-foreground"> {message}</span> : null}
      </span>
      {onRetry ? (
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Retry
        </Button>
      ) : null}
    </div>
  );
}
